import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Alert, Box, LinearProgress, Stack, TextField, Typography } from '../../ui/core';
import api from '../../lib/api';
import useSdpFetch from './useSdpFetch';
import useQueryState, { positiveInt, useSetQueryParams } from './useQueryState';
import { Muted, Pre, SectionTitle } from './parts';
import { FilterBar } from './StateTab';
import { formatCell, formatTs } from './sdpFormatters';

const DEFAULT_WINDOWS = [7, 28];
const DEFAULT_WINDOW = 7;
const HISTORY_LIMIT = 20;

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

const columnsOf = (table, rows) => {
  if (Array.isArray(table.columns) && table.columns.length > 0) {
    return table.columns.filter(isObject).map((c) => ({ key: c.key, label: c.label ?? c.key }));
  }
  const first = rows.find(isObject);
  return first ? Object.keys(first).map((key) => ({ key, label: key })) : [];
};

const Cell = ({ cell }) => {
  const { text, delta } = formatCell(cell);
  return (
    <TableCell>
      {text}
      {delta && (
        <Typography
          component="span"
          color="text.secondary"
          sx={{ fontSize: '12px', ml: '6px' }}
          title="Change vs the prior window"
        >
          {`(${delta})`}
        </Typography>
      )}
    </TableCell>
  );
};

const ReadoutTable = ({ table }) => {
  const rows = Array.isArray(table.rows) ? table.rows.filter(isObject) : [];
  const columns = columnsOf(table, rows);
  return (
    <Box sx={{ mb: '12px' }}>
      {table.title && <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '4px' }}>{table.title}</Typography>}
      {rows.length === 0 ? (
        <Muted>No rows.</Muted>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((c) => (
                  <TableCell key={c.key}>{c.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i} data-testid="readout-row">
                  {columns.map((c) => (
                    <Cell key={c.key} cell={row[c.key]} />
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

const Sections = ({ sections }) => (
  <Stack spacing="8px">
    {sections.filter(isObject).map((section, i) => (
      <Box key={section.key || i}>
        <SectionTitle>{section.title || section.key || 'Section'}</SectionTitle>
        {(Array.isArray(section.tables) ? section.tables : []).filter(isObject).map((table, j) => (
          <ReadoutTable key={j} table={table} />
        ))}
        {typeof section.note === 'string' && section.note && <Muted>{section.note}</Muted>}
      </Box>
    ))}
  </Stack>
);

const ReadoutBody = ({ readout }) => {
  if (Array.isArray(readout.sections)) return <Sections sections={readout.sections} />;
  // No markdown dependency in this app: show renderedMd as preformatted text.
  if (readout.renderedMd) return <Pre>{readout.renderedMd}</Pre>;
  return <Muted>This readout has no content.</Muted>;
};

const historyLabel = (r) => `${formatTs(r.windowEnd)} window end (generated ${formatTs(r.generatedAt)})`;

const ReadoutTab = () => {
  const latest = useSdpFetch(() => api.supplyDemandPace.getReadouts(), 'latest');
  const windows = (latest.data && Array.isArray(latest.data.windows) && latest.data.windows) || [];
  const windowOptions = windows.length ? windows.map((w) => w.windowDays) : DEFAULT_WINDOWS;

  // A window from the URL is taken as-is until the API has listed its
  // windows, then must be one of them (else the first offered).
  const [windowParam] = useQueryState('windowDays', { defaultValue: DEFAULT_WINDOW, parse: positiveInt });
  const windowDays =
    !latest.data || windowOptions.includes(windowParam)
      ? windowParam
      : windowOptions.includes(DEFAULT_WINDOW)
      ? DEFAULT_WINDOW
      : windowOptions[0];
  const [selectedParam, setSelectedId] = useQueryState('readoutId');
  const setParams = useSetQueryParams();

  const history = useSdpFetch(
    () => api.supplyDemandPace.getReadoutHistory({ windowDays, limit: HISTORY_LIMIT }),
    `history-${windowDays}`,
  );
  const historyReadouts = (history.data && Array.isArray(history.data.readouts) && history.data.readouts) || [];

  const onWindowChange = (_e, value) => {
    if (value === null || value === undefined) return;
    setParams({ windowDays: value === DEFAULT_WINDOW ? null : value, readoutId: null });
  };

  // A readout id from the URL that this window's history doesn't hold (once
  // loaded) reads as Latest.
  const selectedId =
    selectedParam && (history.loading || historyReadouts.some((r) => r.id === selectedParam)) ? selectedParam : '';
  const latestForWindow = (windows.find((w) => w.windowDays === windowDays) || {}).readout || null;
  const displayed = selectedId ? historyReadouts.find((r) => r.id === selectedId) || null : latestForWindow;

  return (
    <Stack spacing="8px">
      <FilterBar>
        <ToggleButtonGroup exclusive size="small" value={windowDays} onChange={onWindowChange} aria-label="Readout window">
          {windowOptions.map((days) => (
            <ToggleButton key={days} value={days}>{`${days} days`}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          select
          name="readoutId"
          label="Readout"
          size="small"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 320 }}
          disabled={historyReadouts.length === 0}
        >
          <option value="">Latest</option>
          {historyReadouts.map((r) => (
            <option key={r.id} value={r.id}>
              {historyLabel(r)}
            </option>
          ))}
        </TextField>
      </FilterBar>
      {(latest.loading || history.loading) && <LinearProgress />}
      {latest.error && <Alert severity="error">{latest.error}</Alert>}
      {history.error && <Alert severity="error">{history.error}</Alert>}
      {!latest.loading && !latest.error && !displayed && (
        <Alert severity="info">
          {`No readout yet — the nightly readout job (HITCH-5499) has not produced one for this window (${windowDays} days).`}
        </Alert>
      )}
      {displayed && (
        <Box>
          <Muted>
            {`${displayed.windowDays}-day window ending ${formatTs(displayed.windowEnd)} · generated ${formatTs(
              displayed.generatedAt,
            )} · reference ${displayed.referenceVersion || '—'}`}
          </Muted>
          <ReadoutBody readout={displayed} />
        </Box>
      )}
    </Stack>
  );
};

export default ReadoutTab;
